import { useEffect, useRef } from "react";

import moment from "moment";

import { drugSeparator, yesNoOptions } from "@/App/constants/constants";
import { AgreementTypes, DrugTypes, GeneralTypes, PlanBenefitTypes } from "@/types";

export const sortBySelectedPriceTypes = (
  items: Array<AgreementTypes.PricingClassificationItem>,
  sortByList: Array<string>,
): Array<AgreementTypes.PricingClassificationItem> => {
  const correctOrder = sortByList.reduce((accum: GeneralTypes.Dictionary<number>, priceType: string, i) => {
    accum[priceType] = i;
    return accum;
  }, {});

  const unorderedList: Array<AgreementTypes.PricingClassificationItem> = [];

  const orderedList = items.reduce((accum, item) => {
    if (correctOrder[item.priceType] !== undefined) {
      const idx_order = correctOrder[item.priceType];
      accum[idx_order] = item;
    } else {
      unorderedList.push(item);
    }
    return accum;
  }, []);

  return orderedList.concat(unorderedList);
};

export const formFieldsToInitialize = (
  formFieldNames: Array<string>,
  data: GeneralTypes.Dictionary<string | number | boolean | Array<string>>,
): GeneralTypes.Dictionary<string | number | boolean | Array<string>> => {
  const formValuesToInitialize = formFieldNames.reduce(
    (accum: GeneralTypes.Dictionary<string | number | boolean | Array<string>>, fieldName: string) => {
      if (data[fieldName] !== null) {
        accum[fieldName] = data[fieldName];
      }

      return accum;
    },
    {},
  );

  return formValuesToInitialize;
};

export const updatePriceListEnableStatus = (
  enableStatus: boolean,
  itemId: number,
  priceList: Array<AgreementTypes.PricingClassificationItem>,
): Array<AgreementTypes.PricingClassificationItem> => {
  const updatedListItems = priceList.map((item) => {
    if (itemId === item.id) {
      item.enabled = enableStatus;
    }

    return {
      ...item,
    };
  });

  return updatedListItems;
};

export const isValidDate = (value: string, format = "MM/DD/YYYY"): boolean => {
  return moment(value, format, true).isValid();
};

// Custom React Hook to get previous props or state
// eslint-disable-next-line
export const usePrevious = (value: any): any => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const parseToInt = (value: string | null): number | null => {
  const val = parseInt(value, 10);
  if (isNaN(val)) {
    return null;
  } else {
    return val;
  }
};

// Takes select option value and returns either empty string or LabelType yes/no option object
export const formatYesNoOption = (val: boolean | string | null): string | GeneralTypes.LabelType => {
  const optionsList = Object.values(yesNoOptions);

  // Initial value could be unset, or already formatted as a string locally
  if (val === null || val === undefined) {
    return "";
  } else if (val === "yes" || val === "no") {
    return optionsList.find((option) => option.value === val);
  }

  // If value has been stored as a boolean, format to yes/no option
  const stringValue = val ? "yes" : "no";
  return optionsList.find((option) => option.value === stringValue);
};

export const normalizeNumber = (val: number): number => (val < 0 ? 0 : val);

export const parseNumber = (val: number): number => val || null;

export const validateEvenNumber = (val: number): string | undefined => {
  if (val && val % 2 !== 0) {
    return "Value must be an even number";
  }
  return undefined;
};

export const validateEffectiveUntilDate = (val: string, values: { minimum_claim_submission_date: string }): string | null => {
  const { minimum_claim_submission_date } = values;

  if (moment(new Date(val)).isBefore(new Date(minimum_claim_submission_date))) {
    return "Date Effective Until must be equal to or after the Date Effective From";
  }

  return undefined;
};

export const planBenefitInitializeFormFields = (
  fields: Array<string>,
  selectedPlanBenefit: PlanBenefitTypes.PlanBenefit,
): PlanBenefitTypes.PlanBenefit => {
  return fields.reduce((formValues: { [key: string]: string | {} }, field: string) => {
    if (selectedPlanBenefit[field] !== null && selectedPlanBenefit[field] !== undefined && selectedPlanBenefit[field] !== "") {
      formValues[field] = selectedPlanBenefit[field];
    }

    return formValues;
  }, {});
};

export const checkPlanBenefitFormCompletion = (
  requiredFields: Array<string>,
  completedFields: PlanBenefitTypes.PlanBenefit,
  setCheckbox: Function,
): boolean => {
  return requiredFields.reduce((completed: boolean, field: string): boolean => {
    if (completedFields[field] === null || completedFields[field] === undefined || completedFields[field] === "") {
      completed = false;
      setCheckbox(false);
    }

    return completed;
  }, true);
};

export const transformCommaDelimitedTextToArray = (val: string | Array<string | number> | null): Array<string> => {
  if (!val) {
    return [];
  }

  let transformed = val
    .toString()
    .split(",")
    .map((val) => val.trim());

  if (transformed.length === 1 && transformed[0] === "") {
    transformed = [];
  }

  return transformed;
};

interface PayloadType {
  id?: number;
  description?: string;
}

export const createLabel = <T extends PayloadType>(list: Array<T>): Array<GeneralTypes.LabelType> => {
  return list.map(({ id, description }: PayloadType) => {
    return { label: `${id} - ${description ?? ""}`, value: id };
  });
};

export const mapToLabelsArray = (obj: Object): Array<GeneralTypes.LabelType> => {
  return Object.entries(obj).map((x) => ({ value: x[0], label: x[1] }));
};

export const transformLabelsToNumbersArray = (labelsArray: Array<GeneralTypes.LabelType | string | number>): Array<number> => {
  return labelsArray.map((obj) => (typeof obj === "object" ? Number(obj.value) : Number(obj)));
};

export const transformLabelsToStringsArray = (labelsArray: Array<GeneralTypes.LabelType | string | number>): Array<string> => {
  return labelsArray.map((obj) => (typeof obj === "object" ? `${obj.value}` : `${obj}`));
};

// Calls a sequence of functions and passes the processed value to the next function in the list.
export const pipe
  = (...functions: Array<Function>) =>
  // eslint-disable-next-line
  (value: any): object =>
      functions.reduce((currentValue, func) => func(currentValue), value);

const fakeNullToEmpty = (v: string): string => {
  if (v === "null") {
    return "";
  }

  return v;
};

export const getDrugsFromQueryString = (drugs: string | string[]): Array<DrugTypes.DrugResult> => {
  /*
  Pattern is <ndc dosage dosage_form quantity>.
  Old pattern was <drug_name ndc dosage dosage_form quantity>.
  Throw out the drug_name if it's there.
  */

  if (!drugs) {
    return [];
  }

  if (typeof drugs === "string") {
    drugs = [drugs];
  }

  return drugs.map((drug) => {
    const drugParts = drug.split(drugSeparator);
    if (drugParts.length === 5) {
      return {
        ndc: fakeNullToEmpty(drugParts[1]),
        dosage: fakeNullToEmpty(drugParts[2]),
        dosage_form: fakeNullToEmpty(drugParts[3]),
        quantity: fakeNullToEmpty(drugParts[4]),
      };
    } else if (drugParts.length === 4) {
      return {
        ndc: fakeNullToEmpty(drugParts[0]),
        dosage: fakeNullToEmpty(drugParts[1]),
        dosage_form: fakeNullToEmpty(drugParts[2]),
        quantity: fakeNullToEmpty(drugParts[3]),
      };
    } else {
      return {
        ndc: fakeNullToEmpty(drugParts[0]),
        dosage: fakeNullToEmpty(drugParts[1]),
        dosage_form: fakeNullToEmpty(drugParts[2]),
      };
    }
  });
};

const emptyToFakeNull = (v: string): string => {
  if (v === "" || v === null) {
    return "null";
  }

  return v;
};

export const drugsToQueryString = (drugs: Array<DrugTypes.DrugResult>): Array<string> => {
  return drugs.map((drug) => {
    const { ndc, dosage, dosage_form, quantity } = drug;
    return [emptyToFakeNull(ndc), emptyToFakeNull(dosage), emptyToFakeNull(dosage_form), emptyToFakeNull(quantity)].join(drugSeparator);
  });
};
